import { useCallback, useEffect, useRef, useState } from 'react';
import checkoutService, {
  type CheckoutSession,
  type CreateCheckoutSessionRequest,
} from '@/services/checkout.service';

/** How often to ask the server whether the invoice has been paid. */
const POLL_INTERVAL_MS = 3000;

/**
 * How long to let a saved-card charge run before falling back to the form.
 *
 * A card on file usually settles in a second or two. Showing the payment page
 * underneath it immediately would make a form flash up and vanish, which reads
 * as a failure to the member — so the page waits this long, then appears if the
 * charge has not landed.
 */
const AUTO_CHARGE_GRACE_MS = 8000;

/**
 * Give up watching after this long. The session's own expiry is the real
 * deadline; this only stops the screen polling forever if a member leaves it
 * open on a page they never intend to pay.
 */
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export type CheckoutPhase =
  | 'idle'
  /** Raising the invoice in SmartLev. */
  | 'creating'
  /** A card on file is being charged; no form is shown unless it fails. */
  | 'charging_saved_card'
  /** The member is on SmartLev's page; we are waiting to hear it was paid. */
  | 'awaiting_payment'
  /** Paid, and the booking or order now exists. */
  | 'complete'
  | 'failed';

/**
 * Phases a member can back out of. A saved-card charge is left out on purpose:
 * it is already running on the server, and voiding the invoice under it could
 * take the money for an invoice that no longer exists. The sweep covers it.
 */
const UNFINISHED_PHASES = new Set<CheckoutPhase>(['awaiting_payment', 'failed']);

/**
 * Drives a purchase paid on SmartLev's hosted page.
 *
 * The shape of checkout changes here: the app no longer charges a card and
 * learn the result in the same breath. It raises an invoice, sends the member
 * away to pay it, and waits for the server to be told by SmartLev that the
 * money arrived. Everything the purchase needs to be created travels with the
 * session, so a member who never returns to this screen still gets what they
 * paid for.
 */
export function useSmartLevCheckout() {
  const [phase, setPhase] = useState<CheckoutPhase>('idle');
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideListener = useRef<(() => void) | null>(null);
  /** Id of the session being watched — read by `cancel` without re-binding it. */
  const sessionRef = useRef<string | null>(null);
  const startedAt = useRef<number>(0);
  const live = useRef(true);
  /** Read by the unmount cleanup, which must not re-run on every phase change. */
  const phaseRef = useRef<CheckoutPhase>('idle');
  phaseRef.current = phase;

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    /* Torn down with the polling it belongs to. Left attached it would keep
       reporting a page the member has already navigated away from. */
    if (hideListener.current) {
      document.removeEventListener('visibilitychange', hideListener.current);
      hideListener.current = null;
    }
  }, []);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      stopPolling();
      /* The payment page is embedded in the screen, so leaving the screen is
         backing out of the payment. Without this the invoice stayed payable
         until the expiry sweep. A member who paid on the way out is safe:
         the server refuses to void a paid invoice and leaves it to fulfil. */
      const open = sessionRef.current;
      if (open && UNFINISHED_PHASES.has(phaseRef.current)) {
        sessionRef.current = null;
        void checkoutService.cancelSession(open).catch(() => undefined);
      }
    };
  }, [stopPolling]);

  /**
   * Raise the invoice, and hand its page back for the screen to render inline.
   *
   * Deliberately no `window.open`: this app runs inside a native WebView, where
   * a popup is blocked or opens somewhere the member cannot return from. The
   * screen shows the page in place instead, and polling continues underneath —
   * payment is confirmed by SmartLev's webhook reaching our server, never by
   * anything the page tells the browser.
   */
  const start = useCallback(
    async (payload: CreateCheckoutSessionRequest): Promise<CheckoutSession | null> => {
      setPhase('creating');
      setError(null);

      let created: CheckoutSession;
      try {
        created = await checkoutService.createSession(payload);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            'Could not start checkout. Please try again.',
        );
        setPhase('failed');
        return null;
      }

      // Left the screen while the invoice was being raised.
      if (!live.current) {
        if (!created.autoCharge) {
          void checkoutService.cancelSession(created.id).catch(() => undefined);
        }
        return null;
      }

      setSession(created);

      if (!created.invoiceUrl) {
        setError('No payment page was returned. Please try again.');
        setPhase('failed');
        return null;
      }

      sessionRef.current = created.id;
      setPhase(created.autoCharge ? 'charging_saved_card' : 'awaiting_payment');
      startedAt.current = Date.now();

      stopPolling();

      /* THE ONE SIGNAL A KILLED APP CANNOT SEND.
         Polling stops for two very different reasons: the member left (app
         killed or crashed) and the member stepped into their banking app for
         3-D Secure. Only the second one gets a warning — `visibilitychange`
         fires before iOS suspends the WebView — so saying so here is what
         lets the server keep the long window for a payment in flight and the
         short one for a member who is gone. */
      hideListener.current = () => {
        if (!live.current) return;
        if (document.visibilityState === 'hidden') {
          checkoutService.reportHidden(created.id);
        }
      };
      document.addEventListener('visibilitychange', hideListener.current);

      pollTimer.current = setInterval(async () => {
        if (!live.current) return;

        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          stopPolling();
          setError('We stopped waiting for this payment. Check your orders before paying again.');
          setPhase('failed');
          return;
        }

        try {
          const latest = await checkoutService.getSession(
            created.id,
            typeof document === 'undefined' || document.visibilityState === 'visible',
          );
          if (!live.current) return;
          setSession(latest);

          // The saved card did not take. Hand the member the payment page
          // rather than leaving them watching a spinner.
          if (
            created.autoCharge &&
            latest.status === 'PENDING' &&
            Date.now() - startedAt.current > AUTO_CHARGE_GRACE_MS
          ) {
            setPhase('awaiting_payment');
          }

          if (latest.status === 'FULFILLED') {
            stopPolling();
            setPhase('complete');
            return;
          }

          // PAID but not yet FULFILLED is a normal in-between state — the
          // server is creating the purchase — so it keeps polling.
          if (
            latest.status === 'EXPIRED' ||
            latest.status === 'CANCELED' ||
            latest.status === 'FULFILMENT_FAILED'
          ) {
            stopPolling();
            setError(
              latest.failureReason ??
                (latest.status === 'EXPIRED'
                  ? 'This checkout expired before it was paid.'
                  : 'This checkout could not be completed.'),
            );
            setPhase('failed');
          }
        } catch {
          // A dropped poll is not a failed payment — the money may well be in.
          // Keep trying until the timeout rather than telling the member
          // something went wrong.
        }
      }, POLL_INTERVAL_MS);

      return created;
    },
    [stopPolling],
  );

  /**
   * Stop watching, and close the checkout out on the server.
   *
   * Local state alone was not enough: it left the SmartLev invoice live and
   * payable until the expiry sweep voided it minutes later, for a booking
   * being released right now. Fire-and-forget — the screen has already moved
   * on, and a failed void is picked up by the sweep anyway.
   */
  const cancel = useCallback(() => {
    const open = sessionRef.current;
    stopPolling();
    sessionRef.current = null;
    setPhase('idle');
    setSession(null);
    setError(null);
    if (open) {
      void checkoutService.cancelSession(open).catch(() => undefined);
    }
  }, [stopPolling]);

  return {
    phase,
    session,
    error,
    start,
    cancel,
    /** True while the member is away paying. */
    waiting:
      phase === 'creating' ||
      phase === 'charging_saved_card' ||
      phase === 'awaiting_payment',
    /** True while a card on file is being charged and no form is needed yet. */
    chargingSavedCard: phase === 'charging_saved_card',
  };
}

export default useSmartLevCheckout;
